import React from 'react';
import { InvoiceBatchRegenerationStatusDTO } from '../../../services/apiClient';
import { parseUtc } from '../../../utils/dateUtils/parseUtc';

interface InvoiceBatchRegenerationStatusProps {
  status: InvoiceBatchRegenerationStatusDTO | null | undefined;
}

const formatTime = (value: Date | string | undefined) => {
  const date = parseUtc(value);
  return date
    ? date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';
};

const formatCount = (value: number | undefined) => (value ?? 0).toLocaleString();

const describeChanges = (status: InvoiceBatchRegenerationStatusDTO) => {
  const parts = [
    status.updated ? `${formatCount(status.updated)} updated` : null,
    status.created ? `${formatCount(status.created)} created` : null,
    status.deleted ? `${formatCount(status.deleted)} removed` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : 'no changes';
};

// Apricot is the app's interaction accent; it marks live state, never carries text (too light on white)
const Dot: React.FC<{ className: string }> = ({ className }) => (
  <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-full ${className}`} />
);

/**
 * One quiet line beside the Regenerate button: queued, live progress, the last outcome, or why it stopped.
 */
const InvoiceBatchRegenerationStatus: React.FC<InvoiceBatchRegenerationStatusProps> = ({ status }) => {
  if (!status?.status) return null;

  const wrapper = 'flex items-center gap-2 text-sm whitespace-nowrap w-full sm:w-auto sm:mr-2';

  if (status.isStalled) {
    return (
      <div className={wrapper} role="status" title="No progress for a few minutes. It may retry by itself, or regenerate again.">
        <Dot className="bg-secondary" />
        <span className="text-primary dark:text-slate-100 font-medium">Regeneration stalled</span>
        <span className="text-slate-500 dark:text-slate-400">
          at {formatCount(status.processed)} of {formatCount(status.total)}
        </span>
      </div>
    );
  }

  switch (status.status) {
    case 'Queued': {
      const requested = parseUtc(status.requestedUtc);
      const isScheduled = !!requested && requested.getTime() - Date.now() > 60000;

      return (
        <div className={wrapper} role="status">
          <Dot className="border border-secondary bg-transparent" />
          <span className="text-slate-600 dark:text-slate-300">
            {isScheduled ? `Regeneration scheduled for ${formatTime(status.requestedUtc)}` : 'Regeneration queued'}
          </span>
        </div>
      );
    }

    case 'Running': {
      const total = status.total ?? 0;
      const processed = Math.min(status.processed ?? 0, total);
      const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

      return (
        <div className={wrapper} role="status" aria-live="polite">
          <span className="text-primary dark:text-slate-100 font-medium">Regenerating</span>
          <div
            className="h-1 w-28 shrink-0 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-600"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={processed}
            aria-label="Members regenerated"
          >
            <div
              className="h-full rounded-full bg-secondary transition-[width] duration-700 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="tabular-nums text-slate-500 dark:text-slate-400">
            {formatCount(processed)} / {formatCount(total)}
          </span>
        </div>
      );
    }

    case 'Completed':
      return (
        <div className={`${wrapper} text-slate-500 dark:text-slate-400`} role="status">
          <span>
            Regenerated {formatTime(status.completedUtc)} · {describeChanges(status)}
          </span>
        </div>
      );

    case 'Failed':
      return (
        <div className={wrapper} role="status" title={status.error || undefined}>
          <Dot className="bg-red-500" />
          <span className="font-medium text-red-700 dark:text-red-400">Regeneration failed</span>
          <span className="min-w-0 max-w-[18rem] truncate text-slate-500 dark:text-slate-400">
            {status.error || formatTime(status.completedUtc)}
          </span>
        </div>
      );

    default:
      return null;
  }
};

export default InvoiceBatchRegenerationStatus;
